import { FC, useCallback } from 'react'

import { Box, Button, Dialog, Flex, Icon, Portal, Text, useDisclosure } from '@chakra-ui/react'
import { ExternalLink, HelpCircleIcon } from '@editor/assets/icons'
import { HELP_CENTER_URL } from '@editor/config/constants'
import { TutorialVideoConfig } from '@editor/interfaces/tutorialVideoConfig'
import { hashCode } from '@editor/services/Util'
import { DialogCloser } from '@infraloop/common'
import mixpanel from 'mixpanel-browser'

interface TutorialVideoModalProps extends TutorialVideoConfig {
  useDisclosureProps: ReturnType<typeof useDisclosure>
}

const TutorialVideoModal: FC<TutorialVideoModalProps> = ({
  useDisclosureProps,
  toolType,
  variant,
  version,
  title,
  description,
  videoUrl,
  helpCenterUrl,
}) => {
  const { open, onClose } = useDisclosureProps

  const onModalClose = useCallback(() => {
    // Tracking
    mixpanel.track('Close Help Modal', {
      'Selected Tool': toolType,
      Variant: variant,
      'Video URL': videoUrl,
      'External URL': helpCenterUrl,
      'Help Config Version': version,
    })

    // Close modal
    onClose()
  }, [onClose, toolType, variant, videoUrl, helpCenterUrl, version])

  const openHelpCenterUrl = useCallback(() => {
    // Tracking
    mixpanel.track('Open Help Center', {
      'Selected Tool': toolType,
      Variant: variant,
      'Video URL': videoUrl,
      'External URL': helpCenterUrl,
      'Help Config Version': version,
    })
    // Open help center url
    window.open(helpCenterUrl || HELP_CENTER_URL, '_blank')
  }, [toolType, variant, videoUrl, helpCenterUrl, version])

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(e: { open: boolean }) => {
        if (!e.open) onModalClose()
      }}
      size={videoUrl ? 'xl' : 'xl'}
    >
      <Portal>
        <Dialog.Backdrop zIndex={1401} />
        <Dialog.Positioner>
          <Dialog.Content zIndex={1401}>
            <Dialog.Header color="gray2.700">
              <Flex alignItems="center" fontSize="2xl" fontWeight="bold">
                <Box pr="2">
                  <Icon as={HelpCircleIcon} boxSize="1.5em" />
                </Box>
                {title}
              </Flex>
            </Dialog.Header>
            <DialogCloser />
            <Dialog.Body color="gray2.600">
              {description && description.map((line) => <Text key={hashCode(line)}>{line}</Text>)}
              {videoUrl && (
                <Box pt="16px">
                  <iframe
                    width="100%"
                    height="450"
                    src={videoUrl}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </Box>
              )}
            </Dialog.Body>
            <Dialog.Footer justifyContent="space-between">
              <Button variant="subtle" onClick={onModalClose}>
                閉じる
              </Button>
              <Button
                onClick={openHelpCenterUrl}
                // TODO:variant="outlineStatic"
                bgColor="primary.500"
              >
                <Icon as={ExternalLink} boxSize="1rem" />
                ユーザーマニュアルを開く
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

export default TutorialVideoModal
